/**
 * Survey Distribution Preview Component
 * Shows detailed preview of who will receive surveys with ability to manage ad-hoc recipients
 */

import React, { useState, useEffect } from 'react';
import { X, Send, Users, User, UserCheck, UserPlus, Clock, CheckCircle, AlertCircle, Edit2, Trash2, Plus } from 'lucide-react';
import { previewDistribution, sendSurvey } from '../../services/surveyAPI';
import { searchUsers } from '../../services/cohortAPI';
import { useAuth } from '../../contexts/AuthContext';
import SurveyDistributionProgress from './SurveyDistributionProgress';

const SurveyDistributionPreview = ({ survey, onClose, onConfirm }) => {
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [distributionDetails, setDistributionDetails] = useState(null);
  const [editingAdhoc, setEditingAdhoc] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [enriching, setEnriching] = useState(false);
  
  // Async distribution state
  const [showProgress, setShowProgress] = useState(false);
  const [jobId, setJobId] = useState(null);

  useEffect(() => {
    loadPreview();
  }, [survey.id]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await previewDistribution(survey.id);
      console.log('Distribution preview:', response);
      setPreview(response);
      
      // After initial load, enrich with Graph API data
      enrichPreviewData(response);
    } catch (err) {
      console.error('Error loading preview:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load distribution preview');
    } finally {
      setLoading(false);
    }
  };

  const enrichPreviewData = async (previewData) => {
    if (!previewData?.data?.distribution_preview) return;
    
    // Check if enrichment is needed - skip if no recipients need lookup
    const needsEnrichment = previewData.data.distribution_preview.some(participant =>
      participant.recipients.some(r => r.status === 'needs_lookup')
    );
    
    if (!needsEnrichment) {
      console.log('Preview already enriched, skipping enrichment');
      return;
    }
    
    setEnriching(true);
    const API_URL = process.env.REACT_APP_API_URL;
    const accessToken = await getAccessToken();
    const enrichedPreview = JSON.parse(JSON.stringify(previewData)); // Deep clone
    
    try {
      for (const participant of enrichedPreview.data.distribution_preview) {
        const recipientsToEnrich = [];
        
        // Find recipients that need lookup
        participant.recipients.forEach((recipient, index) => {
          if (recipient.status === 'needs_lookup') {
            recipientsToEnrich.push({ recipient, index });
          }
        });
        
        // Enrich each recipient
        for (const { recipient, index } of recipientsToEnrich) {
          try {
            let url;
            if (recipient.type === 'MANAGER') {
              url = `${API_URL}/leading/api/users/${encodeURIComponent(recipient.participant_email)}/manager`;
            } else if (recipient.type === 'DIRECT_REPORT') {
              url = `${API_URL}/leading/api/users/${encodeURIComponent(recipient.participant_email)}/direct-reports`;
            }
            
            if (url) {
              console.log(`Enriching ${recipient.type} for ${recipient.participant_email}...`);
              const response = await fetch(url, {
                headers: {
                  'Authorization': `Bearer ${accessToken}`
                }
              });
              
              if (response.ok) {
                const result = await response.json();
                const data = result.data || result; // Handle both {data: {...}} and direct response
                
                if (recipient.type === 'MANAGER' && data.manager) {
                  // Update manager
                  console.log('Manager found:', data.manager.name);
                  participant.recipients[index] = {
                    type: 'MANAGER',
                    email: data.manager.email,
                    name: data.manager.name,
                    status: 'ready'
                  };
                  enrichedPreview.data.totals.manager++;
                  enrichedPreview.data.totals.total_recipients++;
                  console.log('Updated preview, triggering re-render');
                } else if (recipient.type === 'DIRECT_REPORT' && data.direct_reports) {
                  // Replace placeholder with actual direct reports
                  participant.recipients.splice(index, 1);
                  data.direct_reports.forEach(report => {
                    participant.recipients.push({
                      type: 'DIRECT_REPORT',
                      email: report.email,
                      name: report.name,
                      status: 'ready'
                    });
                    enrichedPreview.data.totals.direct_reports++;
                    enrichedPreview.data.totals.total_recipients++;
                  });
                } else {
                  // No data found
                  participant.recipients[index] = {
                    ...recipient,
                    status: 'not_found',
                    error: `No ${recipient.type.toLowerCase().replace('_', ' ')} found`
                  };
                }
                
                // Update state after each enrichment for progressive display
                setPreview({ ...enrichedPreview });
              }
            }
          } catch (err) {
            console.error(`Error enriching ${recipient.type}:`, err);
            // Leave as needs_lookup
          }
        }
      }
    } catch (err) {
      console.error('Error during enrichment:', err);
    } finally {
      setEnriching(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError(null);
    
    try {
      // Pass the enriched preview data to avoid Graph API lookups in Lambda
      const response = await sendSurvey(survey.id, preview?.data);
      console.log('Survey distribution response:', response);
      
      setSending(false);
      
      // Check if this is an async response (202 Accepted with job_id)
      // Response comes from axios, so data is in response.data
      const responseData = response.data || response;
      
      if (responseData.job_id && responseData.status === 'PENDING') {
        console.log('Async distribution started, job_id:', responseData.job_id);
        setJobId(responseData.job_id);
        setShowProgress(true);
      } else {
        // Old synchronous response (fallback)
        console.log('Synchronous distribution completed');
        setSent(true);
        setDistributionDetails(responseData);
        
        if (onConfirm) {
          onConfirm();
        }
      }
    } catch (err) {
      console.error('Error sending survey:', err);
      setSending(false);
      setError(err.response?.data?.message || err.message || 'Failed to send survey');
    }
  };

  const handleSearchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await searchUsers(query);
      console.log('Search response:', response);
      // Handle both {users: [...]} and {data: {users: [...]}}
      const users = response.users || response.data?.users || [];
      console.log('Found users:', users.length);
      setSearchResults(users);
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleDistributionComplete = () => {
    console.log('Distribution completed successfully');
    setSent(true);
    setShowProgress(false);
    
    if (onConfirm) {
      onConfirm();
    }
  };

  const handleProgressClose = () => {
    setShowProgress(false);
    onClose();
  };

  const handleAddAdhoc = async (participantId, user) => {
    // Optimistically update UI immediately for instant feedback
    const updatedPreview = JSON.parse(JSON.stringify(preview));
    const participant = updatedPreview.data.distribution_preview.find(p => p.participant_id === participantId);
    if (participant) {
      participant.recipients.push({
        type: 'ADHOC',
        email: user.mail,
        name: user.displayName || `${user.givenName} ${user.surname}`,
        role_label: 'Ad-hoc',
        status: 'ready'
      });
      updatedPreview.data.totals.adhoc = (updatedPreview.data.totals.adhoc || 0) + 1;
      updatedPreview.data.totals.total_recipients++;
      setPreview(updatedPreview);
    }
    
    // Close the search UI immediately
    setEditingAdhoc(null);
    setSearchQuery('');
    setSearchResults([]);
    
    // Then make the API call in the background
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const accessToken = await getAccessToken();
      const response = await fetch(`${API_URL}/leading/api/participants/${participantId}/adhoc-recipients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          survey_id: survey.id,
          employee_id: user.employeeId,
          email: user.mail,
          name: user.displayName || `${user.givenName} ${user.surname}`,
          role_label: 'Ad-hoc',
          title: user.jobTitle
        })
      });
      
      if (response.ok) {
        console.log('Ad-hoc recipient added successfully to database');
      } else {
        const error = await response.json();
        console.error('Failed to add ad-hoc recipient:', error);
        // Revert the optimistic update on failure
        loadPreview();
        alert(error.message || 'Failed to add recipient');
      }
    } catch (error) {
      console.error('Error adding ad-hoc recipient:', error);
      // Revert the optimistic update on failure
      loadPreview();
      alert('Failed to add recipient');
    }
  };

  const handleRemoveAdhoc = (participantId, recipientEmail) => {
    // This would call an API to remove the adhoc recipient
    console.log('Remove adhoc recipient:', participantId, recipientEmail);
    loadPreview();
  };

  const getRecipientIcon = (type) => {
    switch (type) {
      case 'SELF': return <User size={16} />;
      case 'MANAGER': return <UserCheck size={16} />;
      case 'DIRECT_REPORT': return <Users size={16} />;
      case 'ADHOC': return <UserPlus size={16} />;
      default: return <User size={16} />;
    }
  };

  const getRecipientColor = (type) => {
    switch (type) {
      case 'SELF': return { bg: '#f0fdf4', border: '#bbf7d0', text: '#059669' };
      case 'MANAGER': return { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' };
      case 'DIRECT_REPORT': return { bg: '#fef3c7', border: '#fde68a', text: '#d97706' };
      case 'ADHOC': return { bg: '#f3e8ff', border: '#d8b4fe', text: '#7c3aed' };
      default: return { bg: '#f3f4f6', border: '#d1d5db', text: '#374151' };
    }
  };

  const getRecipientLabel = (type) => {
    switch (type) {
      case 'SELF': return 'Self';
      case 'MANAGER': return 'Manager';
      case 'DIRECT_REPORT': return 'Direct Report';
      case 'ADHOC': return 'Ad-hoc';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '0.75rem',
          padding: '2rem',
          textAlign: 'center'
        }}>
          <div className="spinner" style={{ margin: '0 auto' }}></div>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading distribution preview...</p>
        </div>
      </div>
    );
  }

  // Show progress component if async distribution is in progress
  if (showProgress && jobId) {
    return (
      <SurveyDistributionProgress
        surveyId={survey.id}
        jobId={jobId}
        onComplete={handleDistributionComplete}
        onClose={handleProgressClose}
      />
    );
  }

  if (sent) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }} onClick={onClose}>
        <div style={{
          background: '#ffffff',
          borderRadius: '0.75rem',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
          maxWidth: '500px',
          width: '90%',
          padding: '2rem',
          textAlign: 'center'
        }} onClick={(e) => e.stopPropagation()}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <CheckCircle size={48} color="white" />
          </div>
          
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
            Survey Sent Successfully!
          </h2>
          
          <p style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '1.5rem' }}>
            {distributionDetails?.instances_created || 0} survey invitations have been sent.
          </p>

          {distributionDetails?.breakdown && (
            <div style={{
              padding: '1rem',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem',
              textAlign: 'left'
            }}>
              <div style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.5rem' }}>
                <strong>Distribution Summary:</strong>
              </div>
              <div style={{ fontSize: '0.875rem', color: '#6b7280', display: 'grid', gap: '0.25rem' }}>
                {distributionDetails.breakdown.self > 0 && (
                  <div>• Self assessments: {distributionDetails.breakdown.self}</div>
                )}
                {distributionDetails.breakdown.manager > 0 && (
                  <div>• Manager assessments: {distributionDetails.breakdown.manager}</div>
                )}
                {distributionDetails.breakdown.direct_reports > 0 && (
                  <div>• Direct report assessments: {distributionDetails.breakdown.direct_reports}</div>
                )}
                {distributionDetails.breakdown.adhoc > 0 && (
                  <div>• Ad-hoc assessments: {distributionDetails.breakdown.adhoc}</div>
                )}
              </div>
            </div>
          )}
          
          <button onClick={onClose} style={{
            width: '100%',
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #6366f1 0%, #5b21b6 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem'
    }} onClick={onClose}>
      <div style={{
        background: '#ffffff',
        borderRadius: '0.75rem',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
        maxWidth: '1200px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827', margin: 0, marginBottom: '0.25rem' }}>
              Survey Distribution Preview
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
              Review and manage recipients before sending
            </p>
          </div>
          <button onClick={onClose} disabled={sending} style={{
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: sending ? 'not-allowed' : 'pointer',
            opacity: sending ? 0.5 : 1
          }}>
            <X size={18} color="#374151" />
          </button>
        </div>

        {/* Survey Info */}
        <div style={{
          padding: '1.5rem',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Survey Type</div>
              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>{preview?.survey_type}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Cohort</div>
              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>{preview?.data?.cohort_name}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Response Window</div>
              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>{preview?.response_window_days} days</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Total Recipients</div>
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#6366f1' }}>{preview?.data?.totals?.total_recipients || 0}</div>
            </div>
          </div>
        </div>

        {/* Enrichment Progress Bar */}
        {enriching && (
          <div style={{
            padding: '1rem 1.5rem',
            background: '#eff6ff',
            borderBottom: '1px solid #bfdbfe'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <Clock size={16} color="#2563eb" className="animate-pulse" />
              <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1e40af' }}>
                Loading recipient data from EntraID...
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '6px',
              background: '#dbeafe',
              borderRadius: '9999px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 50%, #3b82f6 100%)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s infinite'
              }} />
            </div>
          </div>
        )}

        {/* Totals Summary */}
        <div style={{
          padding: '1rem 1.5rem',
          background: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          {preview?.data?.totals?.self > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: '#f0fdf4',
              border: '1px solid #bbf7d0',
              borderRadius: '0.5rem'
            }}>
              <User size={16} color="#059669" />
              <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                <strong>{preview.data.totals.self}</strong> Self
              </span>
            </div>
          )}
          {preview?.data?.totals?.manager > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: '#dbeafe',
              border: '1px solid #93c5fd',
              borderRadius: '0.5rem'
            }}>
              <UserCheck size={16} color="#1e40af" />
              <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                <strong>{preview.data.totals.manager}</strong> Manager
              </span>
            </div>
          )}
          {preview?.data?.totals?.direct_reports > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: '0.5rem'
            }}>
              <Users size={16} color="#d97706" />
              <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                <strong>{preview.data.totals.direct_reports}</strong> Direct Reports
              </span>
            </div>
          )}
          {preview?.data?.totals?.adhoc > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: '#f3e8ff',
              border: '1px solid #d8b4fe',
              borderRadius: '0.5rem'
            }}>
              <UserPlus size={16} color="#7c3aed" />
              <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                <strong>{preview.data.totals.adhoc}</strong> Ad-hoc
              </span>
            </div>
          )}
        </div>

        {/* Participants List */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1.5rem'
        }}>
          {preview?.data?.distribution_preview?.map((participant, idx) => (
            <div key={participant.participant_id} style={{
              marginBottom: '1.5rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              overflow: 'hidden'
            }}>
              {/* Participant Header */}
              <div style={{
                padding: '1rem',
                background: '#f9fafb',
                borderBottom: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '1rem', fontWeight: '600', color: '#111827' }}>
                      {participant.participant_name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {participant.participant_email}
                    </div>
                  </div>
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                    {participant.recipients.filter(r => r.status === 'ready').length} recipients
                  </div>
                </div>
              </div>

              {/* Recipients List */}
              <div style={{ padding: '1rem' }}>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {participant.recipients.map((recipient, rIdx) => {
                    const colors = getRecipientColor(recipient.type);
                    
                    // Show loading state for recipients being looked up
                    if (recipient.status === 'needs_lookup') {
                      return (
                        <div key={rIdx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.75rem',
                          background: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <Clock size={16} color="#6b7280" className="animate-spin" />
                            <div>
                              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                                {getRecipientLabel(recipient.type)}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                Looking up from EntraID...
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    if (recipient.status === 'not_found') {
                      return (
                        <div key={rIdx} style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '0.75rem',
                          background: '#fef2f2',
                          border: '1px solid #fecaca',
                          borderRadius: '0.375rem'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <AlertCircle size={16} color="#dc2626" />
                            <div>
                              <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                                {getRecipientLabel(recipient.type)}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#dc2626' }}>
                                {recipient.error}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={rIdx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0.75rem',
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '0.375rem'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                          <div style={{ color: colors.text }}>
                            {getRecipientIcon(recipient.type)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
                              {recipient.name}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              {recipient.email}
                            </div>
                            {recipient.role_label && (
                              <div style={{ fontSize: '0.75rem', color: colors.text, marginTop: '0.25rem' }}>
                                Role: {recipient.role_label}
                              </div>
                            )}
                          </div>
                          <div style={{
                            padding: '0.25rem 0.5rem',
                            background: '#ffffff',
                            border: `1px solid ${colors.border}`,
                            borderRadius: '0.25rem',
                            fontSize: '0.625rem',
                            fontWeight: '700',
                            color: colors.text,
                            textTransform: 'uppercase'
                          }}>
                            {getRecipientLabel(recipient.type)}
                          </div>
                        </div>
                        {recipient.type === 'ADHOC' && (
                          <button
                            onClick={() => handleRemoveAdhoc(participant.participant_id, recipient.email)}
                            style={{
                              padding: '0.25rem',
                              background: '#fef2f2',
                              border: '1px solid #fecaca',
                              borderRadius: '0.25rem',
                              cursor: 'pointer',
                              marginLeft: '0.5rem'
                            }}
                            title="Remove"
                          >
                            <Trash2 size={14} color="#dc2626" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Add Ad-hoc Button */}
                {preview?.data?.target_recipients?.includes('ADHOC') && (
                  <div style={{ marginTop: '0.75rem' }}>
                    {editingAdhoc === participant.participant_id ? (
                      <div style={{
                        padding: '1rem',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        borderRadius: '0.375rem'
                      }}>
                        <div style={{ marginBottom: '0.75rem' }}>
                          <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={searchQuery}
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                              handleSearchUsers(e.target.value);
                            }}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem'
                            }}
                          />
                        </div>
                        {searching && (
                          <div style={{ textAlign: 'center', padding: '0.5rem' }}>
                            <div className="spinner" style={{ width: '16px', height: '16px', margin: '0 auto' }}></div>
                          </div>
                        )}
                        {searchResults.length > 0 && (
                          <div style={{
                            maxHeight: '200px',
                            overflowY: 'auto',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.375rem',
                            marginBottom: '0.75rem'
                          }}>
                            {searchResults.map((user, uIdx) => (
                              <div
                                key={uIdx}
                                onClick={() => handleAddAdhoc(participant.participant_id, user)}
                                style={{
                                  padding: '0.75rem',
                                  borderBottom: uIdx < searchResults.length - 1 ? '1px solid #e5e7eb' : 'none',
                                  cursor: 'pointer',
                                  background: '#ffffff'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                onMouseLeave={(e) => e.currentTarget.style.background = '#ffffff'}
                              >
                                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>
                                  {user.displayName || user.name}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  {user.email}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <button
                          onClick={() => {
                            setEditingAdhoc(null);
                            setSearchQuery('');
                            setSearchResults([]);
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#ffffff',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.75rem',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setEditingAdhoc(participant.participant_id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.5rem 1rem',
                          background: '#ffffff',
                          border: '1px dashed #d1d5db',
                          borderRadius: '0.375rem',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#6b7280',
                          cursor: 'pointer',
                          width: '100%',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f9fafb';
                          e.currentTarget.style.borderColor = '#9ca3af';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#ffffff';
                          e.currentTarget.style.borderColor = '#d1d5db';
                        }}
                      >
                        <Plus size={14} />
                        <span>Add Ad-hoc Recipient</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '1rem 1.5rem',
            background: '#fef2f2',
            borderTop: '1px solid #fecaca'
          }}>
            <div style={{ fontSize: '0.875rem', color: '#991b1b' }}>
              <strong>❌ Error:</strong> {error}
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '1.5rem',
          borderTop: '1px solid #e5e7eb',
          background: '#f9fafb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {enriching ? (
              <>
                <Clock size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Loading recipient data...
              </>
            ) : (
              <>
                <Clock size={14} style={{ display: 'inline', marginRight: '0.5rem' }} />
                Estimated send time: ~{Math.ceil((preview?.data?.totals?.total_recipients || 0) / 10)} seconds
              </>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={onClose}
              disabled={sending}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#ffffff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: sending ? 'not-allowed' : 'pointer',
                opacity: sending ? 0.5 : 1
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSend}
              disabled={sending || enriching || (preview?.data?.totals?.total_recipients || 0) === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: (sending || enriching) ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                cursor: (sending || enriching || (preview?.data?.totals?.total_recipients || 0) === 0) ? 'not-allowed' : 'pointer',
                boxShadow: (sending || enriching) ? 'none' : '0 4px 12px rgba(16, 185, 129, 0.25)',
                opacity: enriching ? 0.6 : 1
              }}
            >
              {sending ? (
                <>
                  <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                  <span>Sending...</span>
                </>
              ) : enriching ? (
                <>
                  <Clock size={16} className="animate-pulse" />
                  <span>Loading Data...</span>
                </>
              ) : (
                <>
                  <Send size={16} />
                  <span>Send to {preview?.data?.totals?.total_recipients || 0} Recipients</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyDistributionPreview;
